import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { RefreshCw, ChevronRight, Search } from 'lucide-react';
import { api } from '@/lib/api';
import { Telemetry } from '@/lib/telemetry';
import { useProject } from '@/lib/ProjectContext';
import { useDashboardCache, useEnsureDashboardFresh } from '@/lib/DashboardCacheContext';
import { invalidateConsistency } from '@/lib/consistencyInvalidation';
import { useSubmissionFocus } from '@/lib/SubmissionFocusContext';
import { useSubmissionFilter } from '@/lib/useSubmissionFilter';
import { classifyFindingsByScope } from '@/lib/findingClassification';
import { usePermissions } from '@/lib/usePermissions';
import { useScanStatus, type ScanFeature } from '@/lib/ScanStatusContext';
import { LoadingSpinner } from '@/components/shared/LoadingSpinner';
import type { StudyProfileResponse } from '@/types/api';

/**
 * Chrona Review — Summary (A2F-1262).
 *
 * The Review section index: a vertical feature list mirroring the Word task
 * pane's row-and-badge model (status dot · count badge · scope/provenance line),
 * promoted from the old Dashboard "Chrona Review" card. Each row drills into its
 * existing Review detail page; scope is carried by the global submission-focus
 * context (A2F-454), so navigation needs no scope params.
 *
 * Rows align to the Word task pane labels + order (alphabetical): Abbreviations
 * — inconsistent · Abbreviations — undefined · Cross-References — missing · Dose
 * / Unit · Renames · Study Profile. The first four come from the shared
 * `DashboardCacheContext`; Study Profile is fetched separately (it isn't a
 * consistency-scan feature — no `FEATURE_EXTRACTORS` entry, A2F-1445) and its
 * row counts flagged drift rows (`render` in authority|equivalent, undismissed)
 * across every study, straight from `GET /study-profile`. Contacts (removed
 * from nav) is not a summary row. The generic Reconciliation row was retired
 * with the tab (A2F-1342). Population was fully retired (A2F-1445) — its
 * findings had no successor UI in Study Profile (separate extractors, no
 * shared detection logic) — this row is what replaces its discoverability.
 */

type RowStatus = 'clean' | 'inconsistent' | 'notscanned' | 'na';

interface SummaryRow {
  id: string;
  name: string;
  href: string;
  /** Consistency feature whose scan populates this row (for the inline "Scan"). */
  feature?: ScanFeature;
  status: RowStatus;
  count: number | null;
  sub: string;
}

const DOT_CLASS: Record<RowStatus, string> = {
  clean: 'bg-emerald-500',
  inconsistent: 'bg-brand-orange',
  notscanned: 'border-[1.5px] border-slate-400',
  na: 'border-[1.5px] border-slate-400',
};

export function ReviewSummaryPage() {
  const navigate = useNavigate();
  const { activeProject, loading: projectLoading } = useProject();
  const { data, loading, refresh } = useDashboardCache();
  const ensureFresh = useEnsureDashboardFresh();
  const { submissionFocusId: submissionId, focusedSubmission } = useSubmissionFocus();
  // A2F-1543: tier the consistency findings to the focused submission (same
  // classifier the feature pages use) so the tiles scope to the sub, not the
  // whole-corpus scan summary.
  const { isFiltering, currentDocIds } = useSubmissionFilter();
  const { canRunScans } = usePermissions();
  const scanStatus = useScanStatus();
  const pollTimersRef = useRef<{ timer: ReturnType<typeof setInterval>; feature: ScanFeature }[]>([]);

  // A2F-462: pass the overlay id when the focused sub has superseded files, so
  // re-scans read the lifecycle-resolved view (matches the Dashboard behaviour).
  const [overlayId, setOverlayId] = useState<string | undefined>();
  useEffect(() => {
    const pid = activeProject?.id;
    if (!pid || !submissionId) { setOverlayId(undefined); return; }
    let cancelled = false;
    api.projects.overlayStatus(pid, submissionId)
      .then((status) => { if (!cancelled) setOverlayId(status.hasSupersededFiles ? submissionId : undefined); })
      .catch(() => { if (!cancelled) setOverlayId(undefined); });
    return () => { cancelled = true; };
  }, [activeProject?.id, submissionId]);

  // Ensure the consistency cache is fresh on entry (matches the Dashboard).
  useEffect(() => { ensureFresh(); }, [ensureFresh]);

  // Study Profile isn't a consistency-scan feature (no FEATURE_EXTRACTORS entry,
  // A2F-1445) so it doesn't ride the shared DashboardCacheContext — fetch its
  // cached assembly directly. NOT the lifecycle-vs-overlay `overlayId` above:
  // Study Profile's own scoping (A2F-1447) is a plain submission restriction —
  // StudyProfilePage passes `submissionId ?? undefined` directly, with no
  // cross-scope borrow on a scoped miss — so this must match it exactly, or a
  // focused submission shows a whole-project count here that the page itself
  // then refuses to corroborate.
  const [studyProfile, setStudyProfile] = useState<StudyProfileResponse | null>(null);
  const loadStudyProfile = useCallback(async () => {
    const pid = activeProject?.id;
    if (!pid) { setStudyProfile(null); return; }
    try {
      const r = await api.studyProfile.get(pid, submissionId ?? undefined);
      setStudyProfile(r);
    } catch { /* keep previous cached value on a transient error */ }
  }, [activeProject?.id, submissionId]);
  useEffect(() => { void loadStudyProfile(); }, [loadStudyProfile]);

  // Landing-view telemetry (A2F-1262: the Summary replaced the Dashboard as the
  // default project landing — `review_summary_viewed` replaces `dashboard_viewed`).
  useEffect(() => {
    if (data) Telemetry.reviewSummaryViewed({ submission_id: submissionId ?? undefined });
  }, [data, submissionId]);

  useEffect(() => () => {
    // Clear any in-flight poll timers on unmount.
    pollTimersRef.current.forEach((e) => clearInterval(e.timer));
    pollTimersRef.current = [];
  }, []);

  const pollTask = useCallback((taskId: string, feature: ScanFeature) => {
    const timer = setInterval(async () => {
      try {
        const status = await api.tasks.get(taskId);
        if (status.status === 'completed' || status.status === 'failed' || status.status === 'cancelled') {
          clearInterval(timer);
          pollTimersRef.current = pollTimersRef.current.filter((e) => e.timer !== timer);
          scanStatus.endScan(feature);
          refresh();
          const pid = activeProject?.id;
          if (pid) invalidateConsistency(pid);
          if (feature === 'study-profile') void loadStudyProfile();
        }
      } catch { /* retry next tick */ }
    }, 2000);
    pollTimersRef.current.push({ timer, feature });
  }, [refresh, activeProject?.id, scanStatus, loadStudyProfile]);

  /**
   * Run a set of Review scans. `Scan all` passes every feature; an inline
   * row "Scan" passes just that one. Mirrors the Dashboard's scanAllReview
   * dispatch (direct vs task-based) — the two converge when the Dashboard
   * retires under A2F-1262.
   */
  const runScans = useCallback(async (features: ScanFeature[]) => {
    const pid = activeProject?.id;
    if (!pid) return;
    const oid = overlayId;
    const want = new Set(features);
    const rcApplicable = data?.renameConsistencyApplicable ?? false;

    // Direct scans (return immediately). compound-code feeds the Abbreviations
    // prefix filter + metadata, so it rides along whenever abbreviations scan.
    const direct: Array<[ScanFeature, () => Promise<unknown>]> = [];
    if (want.has('abbreviations')) direct.push(['compound-code', () => api.compoundCode.scan(pid, undefined, true, oid)]);
    if (want.has('cross-references')) direct.push(['cross-references', () => api.crossReferences.scan(pid, undefined, oid)]);
    for (const [feat, fn] of direct) {
      if (scanStatus.startScan(feat)) {
        fn().catch(() => {}).finally(() => { scanStatus.endScan(feat); refresh(); invalidateConsistency(pid); });
      }
    }

    // Task-based scans (LLM; return a taskId we poll).
    const taskScans: Array<[ScanFeature, () => Promise<{ taskId: string }>]> = [];
    if (want.has('abbreviations')) taskScans.push(['abbreviations', () => api.abbreviations.scan(pid, oid)]);
    if (want.has('dose-unit')) taskScans.push(['dose-unit', () => api.doseUnit.scan(pid, oid)]);
    if (want.has('rename-consistency') && rcApplicable) {
      // rename-consistency takes the focused submissionId, not the overlay id.
      taskScans.push(['rename-consistency', () => api.renameConsistency.scan(pid, submissionId ?? undefined)]);
    }
    if (want.has('study-profile')) {
      // Plain submission scoping (A2F-1447), not the lifecycle-vs-overlay `oid` above.
      taskScans.push(['study-profile', () => api.studyProfile.scan(pid, undefined, submissionId ?? undefined, true)]);
    }
    for (const [feat, fn] of taskScans) {
      if (scanStatus.startScan(feat)) {
        try { const { taskId } = await fn(); pollTask(taskId, feat); }
        catch { scanStatus.endScan(feat); }
      }
    }
  }, [activeProject?.id, overlayId, submissionId, refresh, pollTask, scanStatus, data?.renameConsistencyApplicable]);

  const ALL_FEATURES: ScanFeature[] = useMemo(
    () => ['abbreviations', 'cross-references', 'dose-unit', 'rename-consistency', 'study-profile'],
    [],
  );
  const scanning = ALL_FEATURES.some((f) => scanStatus.isScanning(f));

  const scanAll = useCallback(() => {
    runScans(ALL_FEATURES);
  }, [runScans, ALL_FEATURES]);

  const rows: SummaryRow[] = useMemo(() => {
    const abbr = data?.abbreviations ?? null;
    const xref = data?.crossReferences ?? null;
    const dose = data?.doseUnit ?? null;
    const rename = data?.renameConsistency ?? null;
    const rcApplicable = data?.renameConsistencyApplicable ?? true;

    const fromCount = (count: number | null, cleanSub: string, warnSub: (n: number) => string): { status: RowStatus; count: number | null; sub: string } => {
      if (count === null) return { status: 'notscanned', count: null, sub: 'Not scanned yet.' };
      if (count === 0) return { status: 'clean', count: 0, sub: cleanSub };
      return { status: 'inconsistent', count, sub: warnSub(count) };
    };

    // A2F-1543: the tile is a canonical OVERVIEW count per feature, matching the
    // card the detail page shows in each mode — unfocused → the whole-IND stat
    // card (server `summary.*`); focused → the this-submission card
    // (`classifyFindingsByScope(...).thisSub` over undismissed findings). It
    // deliberately does NOT track the detail pages' interactive view state
    // (search / status filter / hide-dismissed / exclude-module-refs) — those
    // narrow a page's live table, not the canonical count. The focused/unfocused
    // dismissal difference mirrors the page's own two cards; the broader
    // "summary.* counts dismissed while tables hide them" inconsistency is
    // pre-existing and product-wide (see follow-up).
    const docIds = isFiltering && currentDocIds ? currentDocIds : null;
    const thisSub = <T extends { documents?: string[]; tier?: import('@/lib/findingClassification').FindingTier | null }>(
      items: T[],
      documentMap: Record<string, string>,
      scope: Set<string>,
      pred: (i: T) => boolean,
    ): number => classifyFindingsByScope(items, documentMap, scope, pred).thisSub;

    const abbrConflicting = docIds && abbr
      ? thisSub(abbr.abbreviations, abbr.documentMap, docIds, (a) => a.status === 'conflicting' && !a.dismissed)
      : (abbr?.summary.conflicting ?? null);
    const abbrUndefined = docIds && abbr
      ? thisSub(abbr.abbreviations, abbr.documentMap, docIds, (a) => a.status === 'undefined' && !a.dismissed)
      : (abbr?.summary.undefined ?? null);
    const xrefMissing = docIds && xref
      ? thisSub(xref.references, xref.documentMap, docIds, (r) => r.status === 'missing' && !r.dismissed)
      : (xref?.summary.missing ?? null);
    const doseInconsistent = docIds && dose
      ? thisSub(dose.substances, dose.documentMap, docIds, (s) => !s.isConsistent && !s.dismissed)
      : (dose?.summary.inconsistent ?? null);

    const renameCount = rename ? rename.renames.filter((r) => r.isInconsistent && !r.dismissed).length : null;

    // Same flagged-render definition as StudyProfilePage's FLAGGED_RENDERS: a
    // directed (authority) or non-directional (equivalent) drift, undismissed.
    const spFlaggedCount = studyProfile
      ? studyProfile.studies.reduce(
          (n, s) => n + s.rows.filter((r) => (r.render === 'authority' || r.render === 'equivalent') && !r.dismissed).length,
          0,
        )
      : null;

    return [
      {
        id: 'abbr-inc', name: 'Abbreviations — inconsistent', href: '/review/abbreviations', feature: 'abbreviations',
        ...fromCount(abbrConflicting,
          'Consistent across the submission.',
          (n) => `${n} abbreviation${n === 1 ? '' : 's'} defined inconsistently.`),
      },
      {
        id: 'abbr-und', name: 'Abbreviations — undefined', href: '/review/abbreviations', feature: 'abbreviations',
        ...fromCount(abbrUndefined,
          'Every abbreviation is defined.',
          (n) => `${n} abbreviation${n === 1 ? '' : 's'} used but never defined.`),
      },
      {
        id: 'xref', name: 'Cross-References — missing', href: '/review/cross-references', feature: 'cross-references',
        ...fromCount(xrefMissing,
          'Every reference resolves to a target.',
          (n) => `${n} reference${n === 1 ? '' : 's'} point to targets not found.`),
      },
      {
        id: 'dose', name: 'Dose / Unit', href: '/review/dose-unit', feature: 'dose-unit',
        ...fromCount(doseInconsistent,
          'Dose / unit values are consistent.',
          (n) => `${n} dose/unit value${n === 1 ? '' : 's'} conflict with their definition.`),
      },
      rcApplicable
        ? {
            id: 'rename', name: 'Renames', href: '/review/rename-consistency', feature: 'rename-consistency',
            ...fromCount(renameCount,
              'No superseded identifiers remain.',
              (n) => `${n} superseded identifier${n === 1 ? '' : 's'} still present.`),
          }
        : {
            id: 'rename', name: 'Renames', href: '/review/rename-consistency', feature: 'rename-consistency',
            status: 'na' as const, count: null,
            sub: 'No predecessor sequence — rename checks not applicable.',
          },
      {
        id: 'study-profile', name: 'Study Profile', href: '/review/study-profile', feature: 'study-profile',
        ...fromCount(spFlaggedCount,
          "Every study's design & enrollment fields agree across sources.",
          (n) => `${n} field${n === 1 ? '' : 's'} drifted from its authoritative source.`),
      },
    ];
  }, [data, studyProfile, isFiltering, currentDocIds]);

  const openFindings = rows.reduce((s, r) => s + (r.status === 'inconsistent' ? (r.count ?? 0) : 0), 0);
  // Exclude not-applicable rows (e.g. Renames with no predecessor) from the
  // count — they aren't a check that ran (A2F-1262 review).
  const checkCount = rows.filter((r) => r.status !== 'na').length;

  if (projectLoading || loading) return <LoadingSpinner message="Loading Review summary..." />;
  if (!activeProject) {
    return (
      <div className="max-w-5xl mx-auto py-16 text-center">
        <p className="text-slate-500">No project selected. Create or select a project in Settings.</p>
      </div>
    );
  }

  return (
    <div className="max-w-3xl">
      {/* Submission-focus banner — scope is implicit on the all-submissions view
          (matches the feature pages); a focus shows the light banner instead. */}
      {focusedSubmission && (
        <div className="flex items-center gap-3 mb-5 px-[18px] py-[14px] bg-orange-50 border border-orange-200 rounded-xl">
          <Search className="w-4 h-4 text-brand-orange flex-none" />
          <span className="text-[15px] text-slate-700">
            Showing data for <b className="font-bold text-brand-charcoal">Submission {focusedSubmission.sequenceNumber}</b>
          </span>
        </div>
      )}

      <div className="flex items-start justify-between gap-6">
        <div>
          <div className="text-[10px] font-semibold tracking-[0.14em] uppercase text-brand-orange mb-1">Checks &amp; Consistency</div>
          <h1 className="text-[25px] font-bold leading-[1.15] tracking-[-0.01em] text-brand-charcoal">Chrona Review</h1>
          <div className="mt-2.5 text-xs text-slate-500">
            {focusedSubmission
              ? <>Read under <span className="font-mono text-slate-600">Seq {focusedSubmission.sequenceNumber}</span>.</>
              : <>Across the IND.</>}
            <span className="text-slate-400"> Findings reflect the checked-in version.</span>
          </div>
        </div>
        {canRunScans && (
          <button
            onClick={scanAll}
            disabled={scanning}
            className="flex-none inline-flex items-center gap-2 px-4 py-[9px] bg-brand-orange text-white rounded-lg text-[13px] font-semibold transition-colors hover:bg-orange-600 disabled:opacity-60 disabled:cursor-not-allowed"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${scanning ? 'animate-spin' : ''}`} />
            {scanning ? 'Scanning…' : 'Scan all'}
          </button>
        )}
      </div>

      <div className="mt-6 bg-white border border-slate-200 rounded-xl">
        <div className="flex items-center justify-between px-[18px] py-[13px] border-b border-slate-100">
          <span className="text-xs font-semibold text-slate-500">
            <span className="font-mono text-slate-600">{checkCount}</span> consistency checks · <span className="font-mono text-slate-600">{openFindings}</span> open findings
          </span>
          <div className="hidden sm:flex items-center gap-4">
            <LegendItem dotClass="bg-emerald-500" label="Clean" />
            <LegendItem dotClass="bg-brand-orange" label="Inconsistent" />
            <LegendItem dotClass="border-[1.5px] border-slate-400" label="Not scanned" />
          </div>
        </div>
        <div className="flex flex-col">
          {rows.map((row) => {
            const rowScanning = row.feature ? scanStatus.isScanning(row.feature) : false;
            const onScan = () => { if (row.feature) runScans([row.feature]); };
            return (
              <SummaryRowItem
                key={row.id}
                row={row}
                canScan={canRunScans}
                scanning={rowScanning}
                onOpen={() => navigate(row.href)}
                onScan={onScan}
              />
            );
          })}
        </div>
      </div>
    </div>
  );
}

function LegendItem({ dotClass, label }: { dotClass: string; label: string }) {
  return (
    <span className="inline-flex items-center gap-1.5 text-[11.5px] font-medium text-slate-500">
      <span className={`w-[9px] h-[9px] rounded-full flex-none ${dotClass}`} />
      {label}
    </span>
  );
}

function SummaryRowItem({
  row, canScan, scanning, onOpen, onScan,
}: {
  row: SummaryRow;
  canScan: boolean;
  scanning: boolean;
  onOpen: () => void;
  onScan: () => void;
}) {
  const clickable = row.status !== 'na';
  const muted = row.status === 'notscanned' || row.status === 'na';

  const main = (
    <>
      <span className={`w-[9px] h-[9px] rounded-full flex-none ${DOT_CLASS[row.status]}`} />
      <span className="flex-1 min-w-0">
        <div className={`text-[15px] font-semibold leading-[1.25] ${muted ? 'text-slate-500' : 'text-brand-charcoal'}`}>{row.name}</div>
        <div className={`mt-[3px] text-[12.5px] leading-[1.4] ${row.status === 'inconsistent' ? 'text-orange-600 font-medium' : 'text-slate-500'}`}>{row.sub}</div>
      </span>
    </>
  );

  // "Stretched-button" row (A2F-1262 review): the whole row — name, badge, AND
  // the chevron — opens the feature, while the inline Scan stays a SIBLING
  // interactive control (no button-in-button). The open button's `after:inset-0`
  // overlay covers the row; the badge + chevron are `pointer-events-none` so
  // clicks fall through to it; the Scan button is `relative z-10` above it.
  return (
    <div
      className={`group relative flex items-center gap-3.5 w-full px-[18px] py-[15px] border-b border-slate-100 last:border-b-0 transition-colors ${
        clickable ? 'hover:bg-orange-50' : ''
      }`}
    >
      {clickable ? (
        <button
          type="button"
          onClick={onOpen}
          aria-label={`Open ${row.name}`}
          className="flex items-center gap-3.5 flex-1 min-w-0 text-left cursor-pointer after:absolute after:inset-0 after:content-['']"
        >
          {main}
        </button>
      ) : (
        <div className="flex items-center gap-3.5 flex-1 min-w-0">{main}</div>
      )}

      {row.status === 'notscanned'
        ? (canScan
            ? (
              <button
                type="button"
                onClick={onScan}
                className="relative z-10 flex-none inline-flex items-center gap-1.5 px-[11px] py-1.5 bg-white text-slate-700 border border-slate-200 rounded-lg text-xs font-semibold transition-colors hover:border-brand-orange hover:text-brand-orange"
              >
                <RefreshCw className={`w-3.5 h-3.5 ${scanning ? 'animate-spin' : ''}`} />
                {scanning ? 'Scanning…' : 'Scan'}
              </button>
            )
            : <span className="pointer-events-none"><Badge variant="na">—</Badge></span>)
        : <span className="pointer-events-none"><CountBadge status={row.status} count={row.count} /></span>}

      {clickable && (
        <ChevronRight aria-hidden className="pointer-events-none w-4 h-4 flex-none text-slate-400 transition-all group-hover:text-brand-orange group-hover:translate-x-0.5" />
      )}
    </div>
  );
}

function CountBadge({ status, count }: { status: RowStatus; count: number | null }) {
  if (count === null) return <Badge variant="na">—</Badge>;
  if (count === 0) return <Badge variant="zero">0</Badge>;
  return <Badge variant="warn">{count}</Badge>;
}

function Badge({ variant, children }: { variant: 'zero' | 'warn' | 'na'; children: React.ReactNode }) {
  const cls = {
    zero: 'bg-slate-100 text-slate-500 font-medium',
    warn: 'bg-orange-100 text-orange-600 font-bold',
    na: 'bg-transparent text-slate-400 font-medium',
  }[variant];
  return (
    <span className={`flex-none inline-flex items-center justify-center min-w-[30px] h-6 px-[9px] rounded-full font-mono text-[13px] tabular-nums ${cls}`}>
      {children}
    </span>
  );
}
